import {getField, updateField} from 'vuex-map-fields';
import sort from 'fast-sort';
import {indexOf, map, first} from 'lodash';
import {
    loadCatalogues,
    loadCatalogue,
    loadCatalogueColumnDefinitions,
    loadCatalogueFilterDefinitions,
    saveCatalogueProjectColumnDefinitions,
    saveCatalogueProjectFilterDefinitions,
    loadCatalogueProjectColumnSet,
    loadCatalogueProjectFilterSet
} from '../../../modules/api/catalogue.api';

function initialState() {
    return {
        catalogues: [],
        catalogue:  null,

        catalogueId: null,
        columnSetId: null,
        filterSetId: null,

        columnDefinitions: [],
        filterDefinitions: [],

        projectColumnDefinitions: [],
        projectFilterDefinitions: [],

        processing: false,
    }
}

const getters = {
    catalogue: (state) => state.catalogue,

    catalogues:        (state) => state.catalogues,
    columnDefinitions: (state) => state.columnDefinitions,
    filterDefinitions: (state) => state.filterDefinitions,

    projectColumnDefinitions: (state) => state.projectColumnDefinitions,
    projectFilterDefinitions: (state) => state.projectFilterDefinitions,

    processing: (state) => state.processing,

    // COLUMNS

    getColumnDefinition: (state) => (columnDefinitionId) => state.columnDefinitions.find(({ id }) => id === columnDefinitionId),

    selectedColumnDefinitions: (state, getters) => sort(state.projectColumnDefinitions.map(({ definitionId }) => {
        return getters.getColumnDefinition(definitionId);
    }).filter(x => !!x)).asc((d) => d.position),

    getProjectColumnDefinition: (state) => (definitionId) => {
        const { projectColumnDefinitions } = state;
        let definitions = projectColumnDefinitions
        .filter((definition) => definition.definitionId === definitionId);

        return first(definitions) || null;
    },

    isProjectColumnActive: (state, getters) => (definitionId) => getters.getProjectColumnDefinition(definitionId) ? true : false,

    getProjectColumnOption: (state, getters) => (definitionId, optionId) => {
        const { optionValues } = getters.getProjectColumnDefinition(definitionId);
        return optionValues.find((value) => value.optionId === optionId) || null;
    },

    getProjectColumnDefinitionByType: (state, getters) => (definationType) => {
        return getters.selectedColumnDefinitions.find(({ type }) => type === definationType) || null
    },

    // FILTER

    getFilterDefinition: (state) => (filterDefinitionId) => state.filterDefinitions.find(({ id }) => id === filterDefinitionId),

    selectedFilterDefinitions: (state, getters) => sort(state.projectFilterDefinitions.map(({ definitionId }) => {
        return getters.getFilterDefinition(definitionId);
    })).asc((d) => d.position),

    getProjectFilterDefinition: (state) => (definitionId) => {
        const { projectFilterDefinitions } = state;
        let definitions = projectFilterDefinitions
        .filter((definition) => definition.definitionId === definitionId);

        return first(definitions) || null;
    },

    isProjectFilterActive: (state, getters) => (definitionId) => getters.getProjectFilterDefinition(definitionId) ? true : false,

    catalogueId: (state) => state.catalogueId,
    columnSetId: (state) => state.columnSetId,
    filterSetId: (state) => state.filterSetId,

    getField,
}

const actions = {
    async loadCatalogues({ commit, rootGetters }) {
        const projectId = rootGetters['projects/project/projectId'];
        return loadCatalogues(projectId).then((data) => commit('SET_CATALOGUES', data));
    },
    async setCatalogueId({ commit, dispatch }, catalogueId) {
        commit('RESET');
        commit('SET_CATALOGUE_ID', catalogueId);
        return dispatch('init');
    },
    async init({ commit, getters }) {
        return loadCatalogue(getters.catalogueId)
        .then((data) => {
            commit('SET_CATALOGUE', data);
            const { columnSet, filterSet } = data;
            commit('SET_COLUMN_ID', columnSet.id);
            commit('SET_FILTER_ID', filterSet.id);
            commit('PROCESSING', true);

            return Promise.all([
                loadCatalogueColumnDefinitions().then((data) => commit('SET_COLUMN_DEFINITIONS', data)),
                loadCatalogueFilterDefinitions().then((data) => commit('SET_FILTER_DEFINITIONS', data)),
                loadCatalogueProjectColumnSet(columnSet.id).then((data) => commit('PREPARE_PROJECT_COLUMN_SET', data)),
                loadCatalogueProjectFilterSet(filterSet.id).then((data) => commit('PREPARE_PROJECT_FILTER_SET', data)),
            ]).finally(() => commit('PROCESSING', false));
        })
    },

    async saveProjectColumns({ getters, rootGetters, commit }) {
        const projectId = rootGetters['projects/project/projectId'];
        const data = {
            projectDefinitions: JSON.parse(JSON.stringify(getters.projectColumnDefinitions)),
            setId:              getters.columnSetId,
            projectId,
        };
        return saveCatalogueProjectColumnDefinitions(data)
        .then((data) => commit('PREPARE_PROJECT_COLUMN_SET', data));
    },

    async saveProjectFilters({ getters, rootGetters, commit }) {
        const projectId = rootGetters['projects/project/projectId'];
        const data = {
            projectDefinitions: JSON.parse(JSON.stringify(getters.projectFilterDefinitions)),
            setId:              getters.filterSetId,
            projectId,
        };
        return saveCatalogueProjectFilterDefinitions(data)
        .then((data) => commit('PREPARE_PROJECT_FILTER_SET', data));
    },

    async save({ dispatch }) {
        return Promise.all([
            dispatch('saveProjectColumns'),
            dispatch('saveProjectFilters'),
        ])
    },

    // COLUMN
    changeProjectColumnDefinitionOption:   ({ commit }, data) => commit('CHANGE_PROJECT_COLUMN_DEFINITION_OPTION', data),
    changeProjectColumnDefinitionPosition: ({ commit }, data) => commit('CHANGE_PROJECT_COLUMN_DEFINITION_POSITION', data),
    selectProjectColumnDefinition:         ({ commit }, data) => commit('SELECT_PROJECT_COLUMN_DEFINITION', data),

    // FILTER
    changeProjectFilterDefinitionOption:   ({ commit }, data) => commit('CHANGE_PROJECT_FILTER_DEFINITION_OPTION', data),
    changeProjectFilterDefinitionPosition: ({ commit }, data) => commit('CHANGE_PROJECT_FILTER_DEFINITION_POSITION', data),
    selectProjectFilterDefinition:         ({ commit }, data) => commit('SELECT_PROJECT_FILTER_DEFINITION', data),

    reset: ({ commit }) => commit('RESET'),
}

const mutations = {
    SET_CATALOGUES:         (state, data) => state.catalogues = data,
    SET_CATALOGUE:          (state, data) => state.catalogue = data,
    SET_COLUMN_DEFINITIONS: (state, data) => state.columnDefinitions = data,
    SET_FILTER_DEFINITIONS: (state, data) => state.filterDefinitions = data,

    SET_COLUMN_ID:    (state, data) => state.columnSetId = data,
    SET_FILTER_ID:    (state, data) => state.filterSetId = data,
    SET_CATALOGUE_ID: (state, catalogueId) => state.catalogueId = catalogueId,

    // COLUMN
    PREPARE_PROJECT_COLUMN_SET:       (state, data) => {
        state.projectColumnDefinitions = [];
        const { projectDefinitions } = data;
        projectDefinitions.map(({ definition, optionValues, position, id }) => {
            let structure = { id, definitionId: definition.id, position, optionValues }
            state.projectColumnDefinitions.push(structure);
        });
    },
    SELECT_PROJECT_COLUMN_DEFINITION: (state, data) => {
        const { projectColumnDefinitions } = state;
        const { id, options } = data;
        const index = indexOf(map(projectColumnDefinitions, (object) => object.definitionId), id);

        if (index !== -1) {
            const { definitionId } = projectColumnDefinitions[index];
            state.projectColumnDefinitions.splice(index, 1);
            const columnDefinition = state.columnDefinitions.find(({ id }) => id === definitionId) || null;
            if (null === columnDefinition) return;

            const filterDefinition = state.filterDefinitions.find(({ type }) => type === columnDefinition.type) || null;
            if (null === filterDefinition) return;

            const filterIndex = indexOf(map(state.projectFilterDefinitions, (object) => object.definitionId), filterDefinition.id);
            if (filterIndex !== -1) state.projectFilterDefinitions.splice(filterIndex, 1);
        } else {
            let structure = { definitionId: id, position: projectColumnDefinitions.length + 1, optionValues: [] }
            structure.optionValues = options.map((option) => ({
                optionId: option.id,
                enabled:  false,
            }));
            state.projectColumnDefinitions.push(structure);
        }
    },

    CHANGE_PROJECT_COLUMN_DEFINITION_OPTION: (state, { definitionId, optionId }) => {
        const { projectColumnDefinitions } = state;
        const definitionIndex = indexOf(map(projectColumnDefinitions, (object) => object.definitionId), definitionId);
        if (definitionIndex !== -1) {
            const projectColumnDefinition = projectColumnDefinitions[definitionIndex];
            const optionIndex = indexOf(map(projectColumnDefinition.optionValues, (object) => object.optionId), optionId);
            if (optionIndex !== -1) {
                const columnOption = projectColumnDefinition.optionValues[optionIndex];
                columnOption.enabled = !columnOption.enabled;
            }
        }
    },

    CHANGE_PROJECT_COLUMN_DEFINITION_POSITION: (state, columnDefinitions) => {
        const projectColumnDefinitions = [];
        for (let i = 0; i < columnDefinitions.length; i++) {
            const { id } = columnDefinitions[i];
            const projectDefinition = state.projectColumnDefinitions.find(({ definitionId }) => definitionId === id) || null;
            if (projectDefinition) {
                projectDefinition.position = i;
                projectColumnDefinitions.push(projectDefinition);
            }
        }
        state.projectColumnDefinitions = projectColumnDefinitions;
    },

    // FILTER
    PREPARE_PROJECT_FILTER_SET:       (state, data) => {
        state.projectFilterDefinitions = [];
        const { projectDefinitions } = data;
        projectDefinitions.map(({ definition, optionValues, position, id }) => {
            let structure = { id, definitionId: definition.id, position, optionValues }
            state.projectFilterDefinitions.push(structure);
        });
    },
    SELECT_PROJECT_FILTER_DEFINITION: (state, data) => {
        const { projectFilterDefinitions } = state;
        const { id, options } = data;
        const index = indexOf(map(projectFilterDefinitions, (object) => object.definitionId), id);

        if (index !== -1) {
            state.projectFilterDefinitions.splice(index, 1);
        } else {
            let structure = { definitionId: id, position: projectFilterDefinitions.length + 1, optionValues: [] }
            structure.optionValues = options.map((option) => ({
                enabled:  false,
                optionId: option.id,
            }));
            state.projectFilterDefinitions.push(structure);
        }
    },

    CHANGE_PROJECT_FILTER_DEFINITION_POSITION: (state, filterDefinitions) => {
        const projectFilterDefinitions = [];
        for (let i = 0; i < filterDefinitions.length; i++) {
            const { id } = filterDefinitions[i];
            const projectDefinition = state.projectFilterDefinitions.find(({ definitionId }) => definitionId === id) || null;
            if (projectDefinition) {
                projectDefinition.position = i;
                projectFilterDefinitions.push(projectDefinition);
            }
        }
        state.projectFilterDefinitions = projectFilterDefinitions;
    },

    PROCESSING: (state, status) => state.processing = status,

    RESET: (state) => {
        const s = initialState();
        Object.keys(s).forEach(key => state[key] = s[key]);
    },

    updateField,
}

export default {
    namespaced: true,
    state:      initialState,
    getters,
    actions,
    mutations
}
